import type { HandbookChapter } from './types';

/**
 * Operator handbook — how to actually do the job, chapter per admin surface.
 *
 * Every step here describes a control that exists on screen today. When an action's
 * preconditions change (which button appears when), update the matching topic in the same
 * PR — a handbook that lies is worse than no handbook. The role chips are derived from
 * `perm` via `PERMS` (`src/lib/roles.ts`), so those at least cannot drift.
 */
export const HANDBOOK: HandbookChapter[] = [
  {
    id: 'c-overview',
    title: 'Genel Bakış',
    intro: 'Giriş yaptığınızda açılan özet ekranı.',
    topics: [
      {
        id: 'h-overview',
        title: 'Özet ekranını okumak',
        when: 'Güne başlarken ya da “şu an ne dönüyor” sorusunu yanıtlarken.',
        // No `perm`: the page is open to every signed-in operator and its figures are scoped
        // server-side to whatever sales that operator's own list would show.
        steps: [
          {
            do: 'Giriş yaptığınızda Genel Bakış açılır; sol menüden her zaman geri dönebilirsiniz.',
          },
          {
            do: 'İhaleler bölümü aktif ve pasif sayılarını verir.',
            note: 'Aktif = yayında, canlı ve duraklatılmış ihalelerin toplamı. Pasif = tamamlanan ve iptal edilenler. Taslaklar hiçbirine dahil değildir, kendi kutusunda durur.',
          },
          {
            do: 'Araçlar bölümünden satılan ve satılmayan araç sayılarını görün.',
          },
          {
            do: 'Satış Tutarı bölümü satılan araçların kazanan tekliflerinin toplamıdır.',
            note: 'Farklı para birimleri ayrı kutularda gösterilir; tek bir toplamda birleştirilmez.',
          },
          {
            do: '“Şu an devam eden ihaleler” listesinden ihalenin adına tıklayarak doğrudan canlı konsola geçin.',
          },
        ],
        warnings: [
          'Sayılar yalnızca sizin görebildiğiniz ihaleleri kapsar. Firma kullanıcısıysanız kendi firmanıza açık olmayan ihaleler hiçbir sayıya girmez.',
        ],
        see: ['h-live-console', 'g-live', 'g-sold'],
      },
    ],
  },

  {
    id: 'c-sale',
    title: 'İhale hazırlama',
    intro: 'İhalenin tanımlanmasından yayına çıkmasına kadar.',
    topics: [
      {
        id: 'h-sale-create',
        title: 'Yeni ihale oluşturma',
        when: 'Yeni bir ihale kurarken.',
        perm: 'salesCreate',
        steps: [
          { do: 'İhaleler → “Yeni İhale”.' },
          {
            do: 'Genel bölümünü doldurun: Başlık, Konum, Tür.',
            note: 'Tür, araçların sıraya nasıl dizileceğini ve dolayısıyla ihalenin ne kadar süreceğini belirler. Sonradan değiştirmek takvimi baştan kurar.',
          },
          {
            do: 'Listeleme zamanı ile Başlangıç zamanını ayrı ayrı girin.',
            note: 'Listeleme = ihalenin vitrinde görünmeye başladığı an. Başlangıç = ilk aracın teklife açıldığı an. İkisi arasında günler olabilir; bu aralıkta katılımcı ihaleyi görür, teklif veremez (üyeler ve teminatı olanlar erken teklif verebilir).',
          },
          {
            do: 'Gerekiyorsa Varsayılan rezerv girin.',
            note: 'Araç bazında rezerv girmezseniz bu değer kullanılır. Rezerv katılımcıya hiçbir zaman gösterilmez.',
          },
          {
            do: 'İhale yalnızca belirli firmalara açıksa Hedef firmalar alanından seçin.',
            note: 'Boş bırakırsanız ihale bütün üyelere açıktır — giriş yapmamış ziyaretçilere değil; hiçbir ihale onlara görünmez. Firma seçilirse ihale genel ihale listesine hiç düşmez, yalnızca seçtiğiniz firmaların üyeleri erişir.',
          },
          { do: 'Canlı yayın yapılacaksa “Ortak konferans odası”nı işaretleyin.' },
          {
            do: 'Araçlar bölümünden araçları seçin; her satırda başlangıç fiyatını, rezervi ve kesin satış işaretini gözden geçirin.',
            note: 'Başlık yanındaki tahmini süre (“~N dk”) seçtiğiniz tür ve araç sayısına göre hesaplanır — ihalenin ne kadar süreceğini buradan görürsünüz.',
          },
          { do: '“İhaleyi Oluştur” ile kaydedin.' },
        ],
        warnings: [
          'Form siz yazarken taslak olarak kendiliğinden kaydedilir. Taslaktaki bir araç bu arada başka bir ihaleye alınmışsa, geri döndüğünüzde listeden çıkarılır ve size bildirilir — araç listesini oluşturmadan önce bir kez daha kontrol edin.',
        ],
        see: ['g-styles', 'g-listing-date', 'g-start-date', 'g-reserve', 'g-company-gated'],
      },
      {
        id: 'h-sale-edit-window',
        title: 'Yayınlanmış ihaleyi düzenleme',
        when: 'İhale yayınlandıktan sonra bir bilgiyi düzeltmeniz gerektiğinde.',
        perm: 'salesEdit',
        steps: [
          { do: 'İhale sayfasını açın; sağ üstteki “…” menüsünden “Düzenle”yi seçin.' },
          { do: 'Alanları düzeltip kaydedin.' },
        ],
        warnings: [
          'Düzenleme yalnızca ihale “Hazırlanıyor” durumundayken **ve** listeleme zamanı henüz gelmemişken açıktır. İhale vitrine düştükten sonra “Düzenle” seçeneği kaybolur — o noktadan sonra katılımcılar ihaleyi görmüş olur ve şartlar değiştirilemez.',
          'Vitrine düşmüş bir ihalede değişiklik gerekiyorsa, ihaleyi iptal edip yenisini kurmak tek yoldur.',
        ],
        see: ['g-listing-date', 'g-preparing', 'f-cannot-edit'],
      },
      {
        id: 'h-sale-cancel',
        title: 'İhale iptali',
        when: 'İhale hiç yapılmayacaksa.',
        perm: 'salesDelete',
        steps: [
          { do: 'İhaleler listesinde ilgili satırın “…” menüsünü açın.' },
          { do: '“İhaleyi iptal et” → onaylayın.' },
        ],
        warnings: [
          'İptal geri alınamaz. Kayıt silinmez, “İptal” olarak işaretlenir ve listelerde görünmeye devam eder.',
          'Tamamlanmış veya zaten iptal edilmiş ihaleler iptal edilemez.',
          'İhalede aktif teminat (bekleyen, bloke ya da tahsil edilmiş) varsa iptali yalnızca Yönetici veya Geliştirici yapabilir; Editör olarak denerseniz işlem reddedilir. Para hareketi olan bir ihaleyi kapatma kararı bilerek daha dar bir yetkiye bağlanmıştır.',
        ],
        see: ['g-cancelled'],
      },
    ],
  },

  {
    id: 'c-live',
    title: 'Canlı konsol',
    intro: 'İhale işlerken kullandığınız koyu renkli yönetim ekranı.',
    topics: [
      {
        id: 'h-live-console',
        title: 'Konsolu okumak',
        when: 'İhale başlamadan hemen önce ve ihale boyunca.',
        perm: 'salesView',
        steps: [
          { do: 'İhaleler listesinden ihalenin adına tıklayın.' },
          {
            do: 'Başlıktaki rozet ihalenin genel durumunu, her araç kartı o aracın kendi durumunu gösterir.',
            note: 'Araç kartında güncel teklif, teklif sayısı, kalan süre ve — geçerliyse — “Kesin satış” rozeti bulunur.',
          },
          {
            do: 'Sayfa kendiliğinden güncellenir; yenilemenize gerek yoktur.',
            note: 'Teklifler ve durum değişiklikleri canlı bağlantı üzerinden anında düşer.',
          },
        ],
        see: ['g-live', 'g-open', 'g-top-bid'],
      },
      {
        id: 'h-live-pause',
        title: 'Duraklatma ve devam ettirme',
        when: 'Salonda bir sorun çıktığında, bağlantı koptuğunda ya da bir itiraz görüşülürken.',
        perm: 'salesLifecycle',
        steps: [
          { do: 'Sayfanın sağ üstündeki “Duraklat”a basın — sayaç durur, teklif alınmaz.' },
          { do: 'Sorun çözülünce “Devam Et”e basın.' },
        ],
        warnings: [
          'Devam ettirdiğinizde takvim yeniden hesaplanır: kalan araçların açılış ve kapanış saatleri kaydırılır.',
          'Duraklamayı yalnızca giriş yapmamış ziyaretçiler görmez; onlara ihale “Devam ediyor” görünmeye devam eder. Giriş yapmış katılımcılar ihaleyi “Duraklatıldı” olarak görür. Yine de duraklamanın sebebini kimse göremez — açıklama gerekiyorsa konferanstan duyurun.',
        ],
        see: ['g-paused', 'f-pause-visible'],
      },
      {
        id: 'h-live-reserve',
        title: 'Rezerv belirleme ve değiştirme',
        when: 'Satıcıyla anlaşılan taban ihale sırasında değiştiğinde.',
        perm: 'salesLifecycle',
        steps: [
          { do: 'Açık aracın kartındaki “Rezerv” düğmesine basın.' },
          { do: 'Yeni tutarı girip onaylayın.' },
        ],
        warnings: [
          'Rezerv katılımcıya hiçbir yerde gösterilmez, ama teklifler onu geçtiği anda lot kendiliğinden kesin satışa döner ve bu katılımcıya “Kesin satış” rozetiyle yansır. Rezervi mevcut teklifin altına indirirseniz lot anında kesin satışa geçer.',
          'Rezerv bir kez geçildikten sonra geri dönüş yoktur: daha yüksek bir rezerv girmek aracı satıştan çıkarmaz, araç yine en yüksek teklifi verene satılır. Rezervi yeniden girmenin anlamı yalnızca henüz geçilmemişken vardır. Pencere açıldığında mevcut rezerv alana yazılı gelir ve durum orada da yazar.',
          'Rezerv değişiklikleri denetim kaydına yazılır.',
        ],
        see: ['g-reserve', 'g-guaranteed', 'h-audit'],
      },
      {
        id: 'h-live-bidding',
        title: 'Konsoldan teklif girme',
        when: 'Salondan, telefondan ya da vekâleten gelen teklifleri sisteme işlerken.',
        perm: 'adminBid',
        steps: [
          {
            do: '“+1 Teklif” — bir sonraki bareme uygun adımı uygular.',
            note: 'Günlük kullanımda en sık kullanacağınız düğme budur.',
          },
          {
            do: '“Hedefe yükselt” — bir hedef tutar girersiniz; sistem ara adımları uygulayıp hedefi aşmayan en yüksek tutarda tek teklif yazar.',
            note: 'Girilen tutar mevcut tekliften büyük olmalıdır.',
          },
          {
            do: '“Serbest” — bareme uymayan bir tutarı doğrudan girer.',
            note: 'Yalnızca Yönetici ve Geliştirici görebilir. Teklif geçmişinde “serbest teklif” olarak ayrışır.',
          },
        ],
        warnings: [
          'Konsoldan girilen teklif gerçek bir tekliftir: katılımcılara anında yansır, geri alınamaz ve kim girdiği denetim kaydına yazılır.',
        ],
        see: ['g-increment', 'g-jump', 'g-free-bid'],
      },
      {
        id: 'h-live-hammer',
        title: 'Kontrolü alma ve çekiç',
        when: 'Bir aracı süresi dolmadan elle kapatmak ya da son saniye kapmasını engellemek istediğinizde.',
        perm: 'salesLifecycle',
        steps: [
          {
            do: 'Açık aracın kartında “Kontrolü al”a basın.',
            note: 'Bu andan itibaren bitişe çok yakın gelen her teklif bitişi ileri atar (son saniye uzatması).',
          },
          {
            do: 'Teklifler durduğunda “Çekiç”e basın — araç anında kapanır.',
            note: '“Çekiç” yalnızca ihale devam ederken, araç açıkken ve kontrolü alınmışken görünür. Üçünden biri eksikse düğme ekranda olmaz.',
          },
          { do: 'Elle yönetimden çıkmak isterseniz “Kontrolü bırak”a basın.' },
        ],
        warnings: [
          'Çekiç geri alınamaz: kapanış normal kapanışla aynıdır, kazanan o anda belirlenir.',
          'Kontrolü almadığınız araçlar süresi dolunca kendiliğinden kapanır — bu güvenlik ağı her zaman devrededir, kontrolü alsanız da yedekte kalır.',
          'Sıralı düzende bir uzatma arkadaki araçları da kaydırır; ihalenin bitiş saati buna göre uzar.',
        ],
        see: ['g-take-control', 'g-soft-close', 'g-hammer', 'f-hammer-missing'],
      },
      {
        id: 'h-live-remove',
        title: 'Aracı ihaleden çıkarma',
        when: 'Araç ihale sırasında satıştan çekildiğinde.',
        // `salesCreate`, not `salesLifecycle`: the console gates the "Çıkar" menu on it and the
        // API's remove route accepts the same roles. The two keys have held the same set since
        // the M17 editor merge — the distinction they encoded was exactly STOCK_EDITOR — but
        // the chip should keep naming the permission the screen actually checks.
        perm: 'salesCreate',
        steps: [
          { do: 'Araç kartındaki “…” menüsünü açın.' },
          { do: '“Çıkar” → onaylayın.' },
        ],
        warnings: [
          'Çıkarılan araç o ihalede teklif almaz ve takvim yeniden hesaplanır.',
        ],
        see: ['g-removed'],
      },
      {
        id: 'h-live-readd',
        title: 'Satılmayan aracı tekrar açma',
        when: 'Araç rezervi geçemeden kapandı ve aynı ihalede bir şans daha verilecek.',
        perm: 'reAdd',
        steps: [
          { do: '“Satılmadı” durumundaki aracın kartında “Tekrar ekle”ye basın.' },
          {
            do: 'İsterseniz kesin satış olarak ekleyin, isterseniz yeni (daha düşük) bir rezerv girin.',
            note: 'Rezerv alanını boş bırakırsanız aracın önceki rezervi devralınır.',
          },
        ],
        warnings: [
          'Tekrar ekleme yalnızca ihale devam ederken çalışır. Duraklatılmış bir ihalede yalnızca sıralı düzenlerde mümkündür — paralel düzende duraklatılmışken tekrar ekleyemezsiniz, önce “Devam Et” demeniz gerekir.',
          'Araç ihaleye yeni bir sıra olarak girer; önceki turun teklifleri taşınmaz.',
        ],
        see: ['g-unsold', 'g-styles', 'f-readd'],
      },
      {
        id: 'h-live-conference',
        title: 'Canlı yayına katılma',
        when: 'İhale konferansla yürütülüyorsa.',
        perm: 'conferenceView',
        steps: [
          {
            do: 'Oda hazır olduğunda ihale sayfasında konferans bağlantısı belirir; tıklayın.',
            note: 'Oda ihale başlamadan kısa süre önce kendiliğinden hazırlanır — elle açmanız gerekmez.',
          },
          { do: 'Araç bazlı oda kullanılıyorsa bağlantı ilgili araç kartında çıkar.' },
        ],
        warnings: [
          'Yayın kaydı uyuşmazlık kanıtı olarak saklanır; ihale konuşmalarını buna göre yürütün.',
        ],
        see: ['g-conference'],
      },
    ],
  },

  {
    id: 'c-vehicle',
    title: 'Araç yönetimi',
    intro: 'Araç kartlarının oluşturulması, ekspertiz raporu ve görseller.',
    topics: [
      {
        id: 'h-vehicle-find',
        title: 'Araç havuzunda filtreleme',
        when: 'Bir ihaledeki araçları toplu görmek ya da listeye girecek müsait araçları ayıklamak istediğinizde.',
        perm: 'vehicleView',
        steps: [
          {
            do: 'Araçlar sayfasındaki arama kutusu plaka, araç kodu, marka, seri ve modelde arar.',
            note: 'Türkçe büyük/küçük harf farkı gözetilmez: “istanbul” yazmak “İstanbul” kayıtlarını da bulur.',
          },
          {
            do: 'İhale: seçtiğiniz ihaleye girmiş araçları listeler.',
            note: 'Çekilmiş ve satılmamış araçlar da listede kalır — o ihalede *neyin olduğunu* sorduğunuz için. “Hiç ihaleye girmemiş” seçeneği ise hiçbir ihaleye alınmamış araçları getirir; silinebilecek araçlar bunların arasındadır, ama hepsi değil — bir firma tarafından gönderilmiş (başvurusu olan) araç, hiç ihaleye girmemiş olsa bile silinemez. Açılır listede en yeni 100 ihale bulunur; daha eskisini filtrelemek isterseniz o ihaleyi içeren bir bağlantıyla gelmeniz yeterlidir, filtre yine çalışır.',
          },
          {
            do: 'Durum: “Müsait” listeye alınabilecek araçlar, “İhalede” planlanmış, açık ya da satılmış bir lotta duranlar.',
            note: 'İhale + Müsait birlikte seçildiğinde o ihaleden çıkıp yeniden listelenebilir hale gelen araçları verir.',
          },
          { do: 'Firma ve model yılı aralığı ile daraltın; filtreler birlikte çalışır.' },
          {
            do: 'Sonuçtaki her satırın “İhale” sütunu, aracın hangi ihalede olduğunu söyler.',
            note: 'Sütun yalnızca sizin de görebileceğiniz ihaleleri adlandırır: ihale listenizde çıkmayan (başka bir firmaya özel) bir ihaledeyse araç “İhalede” görünür ama adı yazmaz. İhale detayını açma yetkiniz varsa ad aynı zamanda bağlantıdır.',
          },
          {
            do: 'İhale, Durum ve Firma seçimleri adres satırına yazılır; sayfanın adresini kopyalayıp bir arkadaşınıza gönderebilirsiniz, bağlantı aynı seçimle açılır.',
            note: 'Arama kutusu ile model yılı adrese yazılmaz; bağlantıyı açan kişi bu iki alanı kendisi doldurur.',
          },
        ],
        see: ['h-vehicle-create', 'h-sale-create'],
      },
      {
        id: 'h-vehicle-create',
        title: 'Araç ekleme ve düzenleme',
        when: 'İhaleye girecek bir aracı sisteme tanıtırken.',
        perm: 'vehicleEdit',
        steps: [
          { do: 'Araçlar → “Yeni Araç”. Mevcut bir aracı düzenlemek için listeden satıra tıklayın.' },
          {
            do: 'Kimlik: plaka, şasi, motor no, araç tipi.',
            note: 'Plaka listelerde ve ihale ekranlarında aracın kimliğidir — doğru girildiğinden emin olun.',
          },
          { do: 'Teknik: marka, seri, model, yıl, renk, km, yakıt, vites, kasa tipi, motor hacmi ve gücü.' },
          { do: 'Donanım: aracın donanım listesini gruplardan işaretleyin.' },
          { do: 'Durum: rehin bankası, yakıt seviyesi, sigorta ve muayene bitiş tarihleri, kontrol listesi.' },
          { do: 'Açıklama: ihale sayfasında katılımcının okuyacağı serbest metin.' },
          { do: 'Kaydedin.' },
        ],
        warnings: [
          'Kaydetmeden sekmeyi kapatır ya da sayfayı yenilerseniz tarayıcı sizi uyarır. Ama uygulama içinde soldaki menüden başka bir sayfaya geçerseniz **uyarı çıkmaz** ve girdikleriniz sessizce kaybolur — araç formundan ayrılmadan önce kaydedin.',
          'Araç silme geri alınamaz ve yalnızca araç hiç ihaleye girmemişse mümkündür.',
        ],
        see: ['h-vehicle-pricing', 'h-vehicle-expertise', 'h-vehicle-media'],
      },
      {
        id: 'h-vehicle-pricing',
        title: 'Fiyat alanları ve fiyat kilidi',
        when: 'Başlangıç fiyatı, gizli rezerv ya da artış miktarını girerken.',
        perm: 'vehicleEdit',
        steps: [
          { do: 'Fiyat bölümünde Başlangıç fiyatı, Gizli rezerv ve Artış miktarını girin.' },
          {
            do: 'Artış miktarını yalnızca bu araç için baremden farklı sabit bir adım istiyorsanız doldurun.',
            note: 'Doldurulursa barem yerine geçer; boş bırakılırsa ihalenin baremi işler.',
          },
        ],
        warnings: [
          'Araç bir ihaleye alındıktan sonra fiyat alanları kilitlenir ve form bunu ekranda söyler. Lot, oluşturulduğu andaki fiyatı üzerine yazar; kartı değiştirmek işleyen bir ihalenin fiyatını değiştirmez. Diğer alanlar (açıklama, görseller, teknik bilgiler) düzenlenmeye açık kalır ve canlı ihaleye yansır.',
        ],
        see: ['g-starting-price', 'g-reserve', 'g-barem', 'f-price-locked'],
      },
      {
        id: 'h-vehicle-expertise',
        title: 'Ekspertiz raporu doldurma',
        when: 'Aracın boya ve değişen durumu kaydedilirken.',
        perm: 'vehicleEdit',
        steps: [
          { do: 'Ekspertiz bölümünde her parça için durumu seçin: orijinal, değişen, boyalı, lokal boyalı, ezik, çizik ya da kayıtlı.' },
          { do: 'Değerlendirilmemiş parçaları boş bırakabilirsiniz; katılımcıya “değerlendirilmedi” olarak görünür.' },
        ],
        warnings: [
          '“Orijinal” rozetini elle veremezsiniz: rozet ancak bütün parçalar orijinal işaretlendiğinde çıkar. Tek bir parça bile farklıysa rozet görünmez.',
        ],
        see: ['g-expertise', 'f-original-badge'],
      },
      {
        id: 'h-vehicle-media',
        title: 'Fotoğraf ve video yönetimi',
        when: 'Aracın görselleri yüklenirken ya da düzenlenirken.',
        perm: 'vehicleEdit',
        steps: [
          {
            do: 'Araç sayfasının en üstündeki Görseller bölümüne dosyaları sürükleyin ya da seçici ile yükleyin.',
            note: 'Görseller formun ilk bölümüdür — aracı açar açmaz doğru araca baktığınızı fotoğraftan doğrulayabilirsiniz.',
          },
          {
            do: 'Bir fotoğrafa tıklayarak tam boy açın; oklarla diğerlerine geçin, Esc ile kapatın.',
            note: 'Küçük görseller kırpılarak dizilir; boyanın ya da hasarın kadraja girip girmediğini ancak tam boy görünümde doğrularsınız.',
          },
          { do: 'Sıralamayı düzenleyin — ilk sıradaki görsel listelerde öne çıkar.' },
          { do: 'İhale listesinde ve araç kartında görünecek ana görseli “kapak” olarak işaretleyin.' },
          {
            do: 'Her görselin “herkese açık” işaretini gözden geçirin.',
            note: 'Bu işaret şu anda kimseye erişim vermez; görselin yayımlanabilir olduğunu kaydeder. Açık artırma sayfalarının tamamı üyelere özeldir — giriş yapmış üye, işaretten bağımsız olarak bütün görselleri görür.',
          },
        ],
        warnings: [
          'Görsel silme geri alınamaz; dosya depodan da kaldırılır.',
          'Giriş yapmamış bir ziyaretçi hiçbir araç fotoğrafını göremez (13.08.2026 kararı): “herkese açık” işareti buna izin vermez. Fotoğrafları paylaşmak isterseniz karşı tarafın üye olması gerekir.',
        ],
        see: ['g-cover', 'g-public-media', 'g-company-gated', 'f-photo-not-public'],
      },
    ],
  },

  {
    id: 'c-submissions',
    title: 'Başvuru moderasyonu',
    intro: 'İş ortaklarının kendi sistemlerinden gönderdiği araçların onaylanması.',
    topics: [
      {
        id: 'h-submissions',
        title: 'Başvuru inceleme, onay ve ret',
        when: 'Başvurular sayfasında bekleyen kayıt olduğunda.',
        perm: 'submissions',
        steps: [
          { do: 'Başvurular → “Bekleyen” sekmesi.' },
          {
            do: 'Satırda aracı gönderen firmanın adını görürsünüz.',
            note: 'Gönderim firmaya ait API anahtarıyla yapılır; hangi personelin gönderdiği kaydedilmez, çünkü anahtar kişiye değil firmaya aittir.',
          },
          {
            do: 'Satırdaki “İncele” ile detay panelini açın: teknik bilgiler, ekspertiz özeti, fotoğraflar ve varsa aktarılamayan görsellerin listesi.',
            note: 'Aktarılamayan görsel uyarısını atlamayın — iki fotoğrafla değerlendirdiğiniz bir aracın aslında beş fotoğrafı gönderilmiş olabilir.',
          },
          { do: 'Uygunsa “Onayla” — araç, hedeflenen ihaleye “Sırada” bir araç olarak eklenir.' },
          { do: 'Uygun değilse reddedin; araç ihaleye hiç girmez.' },
        ],
        warnings: [
          'Onay, aracı doğrudan yayınlanmış bir ihaleye ekler; ayrıca bir yayınlama adımı yoktur.',
        ],
        see: ['g-submission', 'g-scheduled', 'h-firms'],
      },
      {
        id: 'h-firms',
        title: 'Firma kaydı ve API anahtarı verme',
        when: 'Yeni bir iş ortağı araç göndermeye başlayacağında ya da anahtarı yenilenmesi gerektiğinde.',
        perm: 'companyAdmin',
        steps: [
          { do: 'Firmalar → “Yeni Firma” ile kaydı açın: firma adı, vergi dairesi/no ve iletişim bilgileri.' },
          {
            do: 'Firmaya tıklayıp “Anahtar Üret” deyin ve isterseniz bir etiket verin.',
            note: 'Etiket sizin içindir (“stok-servisi” gibi); firmanın birden çok sistemi varsa hangisinin hangi anahtarı kullandığını ayırt etmenizi sağlar.',
          },
          {
            do: 'Ekranda görünen anahtarı kopyalayıp firmaya güvenli bir kanaldan iletin.',
            note: 'Anahtar yalnızca o an gösterilir; bizde yalnızca özeti saklanır.',
          },
          { do: 'Sızma şüphesinde önce yeni anahtar üretin, firma ona geçtikten sonra eskisini “İptal Et” ile kapatın.' },
        ],
        warnings: [
          'Anahtarı pencereyi kapatmadan kaydedin — kurtarılamaz, yenisini üretmeniz gerekir.',
          'Anahtar firmaya aittir, kişiye değil: firmanın personeli değişse de entegrasyon çalışmaya devam eder.',
          'İptal anında geçerlidir; firmanın entegrasyonu yeni anahtara geçmeden araç gönderemez.',
          'Firmayı arşivlemek bütün anahtarlarını da iptal eder. Arşivden çıkarmak eski anahtarları geri getirmez — yeni anahtar üretin.',
        ],
        see: ['h-submissions', 'g-company-gated'],
      },
    ],
  },

  {
    id: 'c-users',
    title: 'Kullanıcılar, teminat ve ödeme',
    intro: 'Katılımcı hesapları, teminat iadeleri ve takılı kalan ödemeler.',
    topics: [
      {
        id: 'h-users',
        title: 'Kullanıcı arama, ekleme ve rol verme',
        when: 'Bir katılımcıyı bulmanız ya da bir personele yetki vermeniz gerektiğinde.',
        perm: 'userAdmin',
        steps: [
          { do: 'Kullanıcılar sayfasında e-posta veya isimle arayın.' },
          {
            do: 'Listeyi Rol, Firma ve Üyelik süzgeçleriyle daraltın; “Filtreleri temizle” hepsini sıfırlar.',
            note: 'Üyelik süzgeci, Ayarlar’daki üyelik paketinin verdiği role bakar (ör. Gold Üyelik → Gold Üye). “Üyeliksiz”, hiçbir paketin rolünü taşımayan hesapları; “Firmasız” ise hiçbir firmaya bağlı olmayanları listeler.',
          },
          { do: 'Yeni hesap için “Yeni Kullanıcı”; mevcut hesabı düzenlemek için satırdaki düzenleme işlemini kullanın.' },
          {
            do: 'Rolleri Yönetim / Firma / Katılımcı gruplarından seçin.',
            note: 'Roller birikimlidir; hiyerarşi yoktur. Bir kişi birden çok rol taşıyabilir.',
          },
          { do: 'Katılımcının geçmişi için isme tıklayıp kullanıcı detay sayfasını açın.' },
        ],
        warnings: [
          'Parolayı değiştirdiğinizde kullanıcının açık oturumları sonlandırılır; yeniden giriş yapması gerekir.',
        ],
        see: ['h-users-approval', 'h-refund', 'h-audit'],
      },
      {
        id: 'h-users-approval',
        title: 'Üyelik başvurusunu onaylama ve reddetme',
        when: 'Siteden kendi kaydını yapan biri sisteme girmeden önce; ya da onaylı bir hesabın erişimini kaldırmanız gerektiğinde.',
        perm: 'userAdmin',
        steps: [
          {
            do: 'Kullanıcılar sayfasındaki “Bekleyen” sekmesini açın. Yeni bir başvuru geldiğinde ayrıca e-posta ile bilgilendirilirsiniz.',
            note: 'Panelden sizin oluşturduğunuz hesaplar bu kuyruğa düşmez; onları oluştururken zaten siz onaylamış olursunuz.',
          },
          {
            do: 'Başvuranın e-postasına tıklayıp geçmişine bakın, sonra “Onayla” ya da “Reddet” deyin.',
          },
          {
            do: 'Reddederken isteğe bağlı bir gerekçe yazabilirsiniz.',
            note: 'Gerekçe yalnızca sizde kalır; başvurana e-posta gönderilmez. Onayladığınızda ise başvurana “üyeliğiniz onaylandı” e-postası gider.',
          },
          {
            do: 'Kararı sonradan değiştirmek için ilgili sekmeye gidin: “Onaylı” sekmesinde “Reddet”, “Reddedilen” sekmesinde “Onayla” düğmesi çıkar.',
            note: 'Aynı kararı ikinci kez veremezsiniz — zaten onaylı bir hesabın satırında “Onayla” düğmesi hiç görünmez. İki kişi aynı anda **aynı** kararı verirse yalnızca biri işler, diğerine “bu hesap az önce başkası tarafından karara bağlandı” uyarısı çıkar. Ancak ikisi **farklı** karar verirse ikisi de geçerli sayılır ve en son verilen karar kalır; sistem bunu “yanlışlıkla üzerine yazma” değil, bilinçli bir geri alma sayar. Kimin ne zaman ne yaptığı denetim kaydında durur — tereddüt ettiğinizde oraya bakın.',
          },
        ],
        warnings: [
          'Onaylanmayan bir hesap giriş yapamaz ve teklif veremez; kayıt olmuş olması tek başına yetmez.',
          'Onaylı bir hesabı reddettiğinizde açık oturumları kapatılır, ancak elindeki oturum anahtarı en fazla 15 dakika daha geçerli kalabilir. Canlı bir ihale sırasında acil bir durum varsa aracı ihaleden çıkarmak daha kesin bir çözümdür.',
          'Yanlışlıkla reddettiğiniz bir başvuruyu “Reddedilen” sekmesinden yeniden onaylayabilirsiniz.',
          'Kendi hesabı reddedilmiş bir yönetici hiçbir başvuruyu — kendisininki dâhil — onaylayamaz; bu işlemler ona kapanır. Bir yöneticinin erişimini yanlışlıkla kaldırdıysanız onu başka bir yöneticinin geri onaylaması gerekir.',
        ],
        see: ['h-users', 'h-audit', 'g-approval-pending'],
      },
      {
        id: 'h-refund',
        title: 'Teminat iadesi',
        when: 'Katılımcı ihaleyi kazanamadı ya da katılımdan vazgeçti ve teminatı geri isteniyor.',
        perm: 'depositRefund',
        steps: [
          { do: 'Kullanıcılar → kullanıcıya tıklayın → Teminatlar bölümü.' },
          { do: '“Bloke” durumdaki satırda “İade Et”e basıp onaylayın.' },
          {
            do: 'Sonucu ekrandaki bildirimden okuyun.',
            note: 'Para bankadan henüz çekilmemişse işlem iptal (void), çekilmişse iade olarak sonuçlanır. Bu seçimi sistem yapar.',
          },
        ],
        warnings: [
          'İade gerçek para hareketidir ve geri alınamaz.',
          'Katılımcı o araçta en yüksek teklifin sahibiyse iade engellenir — önce lotun kapanmasını bekleyin.',
          'Banka yanıtı alınamazsa teminat “Mutabakat bekliyor” rozetiyle kilitlenir. Bu durumda satırdaki “Mutabakat” düğmesiyle bankaya durum sorulur; sonuç net değilse banka panelinden kontrol edin ve 10 dakika sonra tekrar deneyin.',
        ],
        see: ['g-refund', 'g-reconcile', 'g-deposit-states', 'f-refund-blocked'],
      },
      {
        id: 'h-payments',
        title: 'Takılı kalan ödemeler',
        when: 'Katılımcı “ödeme yapamıyorum” diyorsa ya da bir ödeme “3DS sürüyor” durumunda kalmışsa.',
        perm: 'depositRefund',
        steps: [
          { do: 'Kullanıcı detay sayfasında Ödemeler bölümünü açın.' },
          {
            do: '“Takılı” işaretli satırda önce “Mutabakat”ı deneyin.',
            note: 'Banka tahsilatı doğrularsa ödeme tamamlanır ve hak kazanımı işlenir; para iade edilmişse kayıt kapanır ve kullanıcı yeniden deneyebilir.',
          },
          {
            do: 'Mutabakat sonuç vermiyorsa ve bankada hiç tahsilat olmadığını **banka panelinden doğruladıysanız** “Başarısız işaretle” kullanılabilir.',
            note: 'Günlük otomatik tarama bu kayıtların çoğunu kendiliğinden toparlar; elle müdahale son çaredir.',
          },
        ],
        warnings: [
          '“Başarısız işaretle”yi bankada tahsilat varken kullanmak çifte tahsilata yol açar. Banka panelinden doğrulamadan kullanmayın.',
          'Takılı bir ödeme çözülene kadar kullanıcı yeni ödeme başlatamaz — şikâyetin çoğu zaman asıl sebebi budur.',
        ],
        see: ['g-3ds', 'g-reconcile', 'f-stuck-3ds'],
      },
    ],
  },

  {
    id: 'c-audit',
    title: 'Denetim kaydı',
    intro: 'Kimin ne zaman ne yaptığının kaydı.',
    topics: [
      {
        id: 'h-audit',
        title: 'Denetim kaydında arama',
        when: 'Bir işlemin kim tarafından yapıldığını ya da bir itirazın geçmişini araştırırken.',
        perm: 'auditView',
        steps: [
          { do: 'Denetim sayfasını açın.' },
          {
            do: 'Varlık türü, varlık id, kullanıcı id, **firma (API)**, işlem ve tarih aralığı ile daraltın.',
            note: 'Firma süzgeci, bir iş ortağının API anahtarıyla yaptığı gönderim/düzeltme/geri çekme işlemlerini tek başına listeler.',
          },
          {
            do: 'Varlık türü ve işlem süzgeçlerinde alan tıklandığında Türkçe karşılıklarıyla birlikte bir öneri listesi açılır; listeden seçin.',
            note: 'Süzgeç kayıttaki teknik değere göre çalışır (örneğin “Teklif verildi” için `bid-placed`), bu yüzden alana elle Türkçe yazmak sonuç getirmez — listeden seçmek her zaman doğrudur.',
          },
          {
            do: 'Kullanıcı sütununda işlemi yapan kişinin adı ve soyadı, altında da kullanıcı id’si yazar.',
            note: 'Adı girilmemiş bir kullanıcı e-postasıyla görünür. Süzgeç ve paylaşılabilir bağlantı her zaman alttaki id’yi kullanır — aramak için onu kopyalayın.',
          },
          { do: 'Satırdaki detayı açarak değişikliğin öncesi ve sonrası değerlerini görün.' },
          {
            do: 'İhale ve kullanıcı sayfalarındaki “Denetim kaydı” bağlantısı sizi doğrudan o kaydın filtrelenmiş görünümüne götürür.',
            note: 'Filtreli adres paylaşılabilir — bir meslektaşınıza doğrudan bu bağlantıyı gönderebilirsiniz.',
          },
        ],
        warnings: [
          'Kayıtlar değiştirilemez ve silinemez. Bazı hassas alanlar maskelenmiş görünür.',
          'Kim sütununda “sistem” yazan satırlar otomatik işlerdir (zamanlanmış görevler, ihale saati gelince çalışan adımlar). Bir iş ortağının anahtarıyla gelen satır ise “… (firma anahtarı)” olarak görünür — ikisini karıştırmayın.',
          'Hesabı silinmiş bir kullanıcının satırı adsız, yalnızca id ile görünür. Kayıt durur, kimlik bilgisi gösterilmez.',
        ],
        see: ['g-audit', 'g-api-key', 'h-firms'],
      },
    ],
  },

  {
    id: 'c-config',
    title: 'Ayarlar',
    intro: 'Teminat tutarı, üyelik paketleri ve vitrin sayaçları.',
    topics: [
      {
        id: 'h-config',
        title: 'Teminat tutarı ve üyelik paketleri',
        when: 'Varsayılan teminat ya da üyelik fiyatları değiştiğinde.',
        perm: 'configAdmin',
        steps: [
          { do: 'Ayarlar → Depozito Tutarı bölümünden varsayılan teminatı girin ve kaydedin.' },
          { do: 'Üyelik Paketleri tablosunda kod, ad, fiyat, rol ve aktiflik alanlarını düzenleyin.' },
          { do: 'Yeni paket için “Paket Ekle”; kaldırmak için satırdaki kaldırma işlemini kullanın.' },
          { do: 'Değişiklikler ancak “Kaydet” ile kalıcı olur.' },
        ],
        warnings: [
          'Sayfa üstünde “Kaydedilmemiş değişiklikler var” uyarısı varken sayfadan ayrılırsanız değişiklikler kaybolur.',
          'Teminat tutarı değişikliği yalnızca bundan sonra alınacak teminatları etkiler; mevcut bloke tutarlar değişmez.',
        ],
        see: ['g-deposit', 'g-membership', 'h-showcase-counters'],
      },
      {
        id: 'h-showcase-counters',
        title: 'Vitrin sayaçlarını yönetme',
        when: 'Giriş yapmamış ziyaretçiye toplam rakamları göstermek ya da gizlemek istediğinizde.',
        perm: 'configAdmin',
        steps: [
          {
            do: 'Ayarlar → Vitrin Sayaçları bölümünü açın. Her satırda o sayacın gerçek değeri ile ziyaretçinin göreceği değer yan yana durur.',
            note: 'Ziyaretçiye gösterilen sayı = başlangıç değeri + gerçek sayı. Gerçek sayı arttıkça gösterilen sayı da artar; elle güncellemeniz gerekmez.',
          },
          {
            do: 'Göstermek istediğiniz sayacı işaretleyin ve bir başlangıç değeri girin.',
            note: 'Sağdaki “Ziyaretçi görür” sütunu siz yazarken güncellenir — kaydetmeden önce sonucun nasıl okunduğuna bakın.',
          },
          { do: 'Değişiklikler ancak “Kaydet” ile kalıcı olur.' },
        ],
        warnings: [
          'Sayaçlar varsayılan olarak kapalıdır ve hiçbiri açık değilken vitrinde bu bölüm hiç görünmez. Kapalı bir sayaç “0” olarak da çıkmaz.',
          'Başlangıç değeri negatif olamaz: halka açık bir sayacın geriye gitmesi, açıklaması olmayan bir durumdur.',
          'Rakamlar bir bütün olarak okunur. Çok sayıda ihaleye karşılık çok az satış göstermek, hiç göstermemekten daha kötü bir izlenim bırakır — iki sayacı birlikte değerlendirin.',
          'Sayaçlar yalnızca toplamdır; hangi ihale, hangi araç ya da hangi firma olduğu dışarıya hiçbir şekilde çıkmaz. Firmaya özel ihaleler de toplamlara dahildir.',
          'Değişiklik denetim kaydına yazılır: kimin neyi ne zaman değiştirdiği bellidir.',
        ],
        see: ['h-config', 'h-audit', 'g-showcase-counter'],
      },
    ],
  },

  {
    id: 'c-export',
    title: 'Raporlama',
    intro: 'İhale sonuçlarının dışa aktarılması.',
    topics: [
      {
        id: 'h-export',
        title: 'Excel çıktısı alma',
        when: 'İhale sonuçlarını, teminatları ve teklif geçmişini dışarı aktarırken.',
        perm: 'exportSale',
        steps: [
          { do: 'İhale sayfasında sağ üstteki “…” menüsünden “Excel İndir”i seçin.' },
          {
            do: 'Dosya üç sayfa halinde iner: Detaylar, Depozitolar ve Teklifler.',
            note: 'Dosya tarayıcınızda oluşturulur; sunucuya bir istek gitmez ve bekleme olmaz.',
          },
        ],
        warnings: [
          'Çıktı indirildiği andaki veriyi içerir. Devam eden bir ihalede aldığınız dosya birkaç dakika içinde eskir.',
        ],
        see: ['g-closed'],
      },
    ],
  },
];
